import * as React from "react"
import { clsx } from "clsx"
import {
  Card as ShadcnCard,
  CardHeader as ShadcnCardHeader,
  CardContent as ShadcnCardContent,
  CardFooter as ShadcnCardFooter,
  CardTitle as ShadcnCardTitle,
  CardDescription as ShadcnCardDescription,
} from "@/components/ui/card"

/* ---------------------------------- Card --------------------------------- */

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: "default" | "glass" | "light"
  padding?: "none" | "sm" | "md" | "lg"
  hoverable?: boolean
}

export const Card = ({
  variant = "default",
  padding = "md",
  hoverable = false,
  className,
  ...props
}: CardProps) => {
  const variants = {
    // ✅ semantic tokens: chạy tốt cả dark/light
    default:
      "bg-card/70 text-card-foreground backdrop-blur-xl border border-border rounded-2xl",
    /**
     * Giữ class custom của bạn, nhưng thêm fallback semantic để light không bị fail
     * (nếu glass-panel đang hardcode màu tối)
     */
    glass:
      "glass-panel bg-card/60 text-card-foreground border border-border/60 backdrop-blur-xl rounded-2xl",
    /**
     * Variant light: vẫn giữ glass-panel-light nhưng đảm bảo semantic foreground/border
     */
    light:
      "glass-panel-light bg-card text-card-foreground border border-border backdrop-blur-xl rounded-2xl",
  }

  const paddings = {
    none: "",
    sm: "p-4",
    md: "p-6",
    lg: "p-8",
  }

  return (
    <ShadcnCard
      className={clsx(
        variants[variant],
        paddings[padding],
        hoverable && "glass-panel-hover cursor-pointer",
        className
      )}
      {...props}
    />
  )
}

/* ------------------------------- CardHeader ------------------------------- */

export const CardHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <ShadcnCardHeader className={clsx("mb-4", className)} {...props} />
)

/* -------------------------------- CardTitle ------------------------------- */

export const CardTitle = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLHeadingElement>) => (
  <ShadcnCardTitle
    // ✅ token chuẩn shadcn, không hardcode dark
    className={clsx("text-lg font-semibold text-card-foreground", className)}
    {...props}
  />
)

/* ----------------------------- CardDescription ----------------------------- */

export const CardDescription = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLParagraphElement>) => (
  <ShadcnCardDescription
    // ✅ token chuẩn shadcn
    className={clsx("text-sm text-muted-foreground mt-1", className)}
    {...props}
  />
)

/* ------------------------------ CardContent ------------------------------- */

export const CardContent = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <ShadcnCardContent className={className} {...props} />
)

/* ------------------------------- CardFooter ------------------------------- */

export const CardFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <ShadcnCardFooter
    // ✅ border semantic
    className={clsx("mt-4 pt-4 border-t border-border", className)}
    {...props}
  />
)
